package pl.nickers.match3.ecs

import kotlin.random.Random

/**
 * Single consumer of `MatchNeighbourComponent` (on ice) and `HitByAreaEffectComponent` —
 * decrements [IceCubeComponent.life] once per round and calls [restoreBrokenIceCube]
 * when life hits zero. The marker is cleared from each ice as it is processed so a
 * cascading RESOLVE→GRAVITY→RESOLVE cycle does not double-damage the same cube.
 */
internal class IceDamageSystem(
    private val random: Random = Random.Default,
    private val catalog: EntityCatalog = EntityCatalog.default(),
    private val pieceSet: PieceSet = PieceSetLoader.default(),
) : BaseSystem() {
    private lateinit var iceCubeMapper: ComponentMapper<IceCubeComponent>
    private lateinit var matchNeighbourMapper: ComponentMapper<MatchNeighbourComponent>
    private lateinit var hitByAreaEffectMapper: ComponentMapper<HitByAreaEffectComponent>
    private lateinit var jellyTypeMapper: ComponentMapper<JellyTypeComponent>
    private lateinit var bodyImageMapper: ComponentMapper<BodyImageComponent>
    private lateinit var jellyFaceMapper: ComponentMapper<JellyFaceComponent>
    private lateinit var draggableMapper: ComponentMapper<DraggableComponent>
    private lateinit var pieceIdMapper: ComponentMapper<PieceIdComponent>

    override fun initialize() {
        iceCubeMapper = world.mapper()
        matchNeighbourMapper = world.mapper()
        hitByAreaEffectMapper = world.mapper()
        jellyTypeMapper = world.mapper()
        bodyImageMapper = world.mapper()
        jellyFaceMapper = world.mapper()
        draggableMapper = world.mapper()
        pieceIdMapper = world.mapper()
    }

    override fun processSystem() {
        val byMatch = world.getEntitiesForAspect(
            Aspect.all(IceCubeComponent::class, MatchNeighbourComponent::class),
        )
        val byBlast = world.getEntitiesForAspect(
            Aspect.all(IceCubeComponent::class, HitByAreaEffectComponent::class),
        )
        val targets = (byMatch.toSet() + byBlast.toSet())
        if (targets.isEmpty()) return
        for (entityId in targets) {
            // Piece-def gates the lifecycle. Only entities whose definition declares
            // onActivate: DecrementLife participate; everything else is left alone.
            val onActivate = world.onActivateOf(entityId, pieceSet)
            if (onActivate?.effect != EffectKind.DECREMENT_LIFE) continue
            val ice = iceCubeMapper[entityId] ?: continue
            matchNeighbourMapper.remove(entityId)
            hitByAreaEffectMapper.remove(entityId)
            ice.life -= 1
            if (ice.life > 0) continue

            // Destruction path: read onDestroyed and dispatch on its effect.
            iceCubeMapper.remove(entityId)
            val onDestroyed = world.onDestroyedOf(entityId, pieceSet)
            when (onDestroyed?.effect) {
                EffectKind.TRANSFORM_INTO -> {
                    // Only SpawnPool is supported during the transition; other
                    // selectors will land alongside their effect implementations.
                    when (onDestroyed.selector) {
                        is PieceSelector.SpawnPool, null -> restoreBrokenIceCube(
                            entityId = entityId,
                            jellyTypeMapper = jellyTypeMapper,
                            bodyImageMapper = bodyImageMapper,
                            jellyFaceMapper = jellyFaceMapper,
                            draggableMapper = draggableMapper,
                            pieceIdMapper = pieceIdMapper,
                            random = random,
                            catalog = catalog,
                        )
                        else -> { /* Selector unsupported yet; piece survives as a stub. */ }
                    }
                }
                EffectKind.REMOVE -> world.deleteEntity(entityId)
                else -> { /* No onDestroyed → entity becomes inert (no IceCubeComponent left). */ }
            }
        }
    }
}
